import React, { useState, useEffect } from "react";
import { BaselineChart } from "./BaselineChart";
import { Navbar } from "../Navigation/Navbar";
import { Button, Divider, Empty, Popconfirm, Select } from "antd";
import { DatePicker, Space } from "antd";
import dayjs from "dayjs";
import "../../Styles/styles-TeamData.css";
import "../../Styles/styles-PostData.css";
import { renderAsPDF } from "../../Utils/pdf_utils";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { DataColorUtil } from "../../Utils/color_utils";
import {
  emailBaselineChart,
  getBaselineData,
  setBaselineFilterDate,
  setBaselineFilterPlayerGroup,
  setBaselineFilterPosition,
  setBaselineSortBy,
} from "../../store/actions/baseline.actions";
import { PositionSelect } from "../PositionSelect/PositionSelect";
import { HelpButton } from "../Common/HelpButton";
import { useDataAccess, DataAccessDenied } from "@components/PermissionedComponents";

const { Option } = Select;

export const Baseline = () => {
  const dispatch = useDispatch();
  const [dataColorUtil, setDataColorUtil] = useState(null);

  const {
    filter: { date, position, playerGroup },
    sortBy,
    data,
  } = useSelector(({ baseline }) => baseline);

  const {
    positions,
    playerGroups,
    sport,
    info: { team_colors: teamColors, display_name: teamName },
    color_strategies: colorStrategies
  } = useSelector(({ team }) => team);

  const { isDenied: isDataDenied } = useDataAccess();

  useEffect(() => {
    if (data?.colors && data?.metrics?.length && colorStrategies?.length) {
      setDataColorUtil(new DataColorUtil({
        teamName,
        gpsBaselines: data.colors,
        teamGpsMetrics: data.metrics,
        colorStrategies
      }));
    } else {
      setDataColorUtil(null);
    }
  }, [data?.colors, data?.metrics, teamName, colorStrategies]);

  const [rendering, setRendering] = useState(false);

  const dateFormat = "YYYY-MM-DD";

  const get = () => {
    dispatch(getBaselineData());
  };

  const email = () => {
    dispatch(emailBaselineChart());
  };

  const onDateChange = (date, dateString) => {
    dispatch(setBaselineFilterDate(dateString === "" ? null : dateString));
  };

  const positionChange = (value) => {
    dispatch(setBaselineFilterPosition(value));
  };

  const playerGroupChange = (value) => {
    dispatch(setBaselineFilterPlayerGroup(value));
  };

  const sortByChange = async (value) => {
    await dispatch(setBaselineSortBy(value));
    get();
  };

  const handleDownloadPDF = async () => {
    setRendering(true);

    let descString = `Date: ${date}`;
    if (position) descString = `${descString} | Position: ${position}`;
    if (playerGroup)
      descString = `${descString} | Player Group : ${playerGroup}`;

    await new Promise((resolve) => setTimeout(resolve, 300));

    let widget = null,
      table = null;

    const chartDOMs = document.getElementsByClassName(
      "w-100"
    );
    await renderAsPDF({header: descString, widget, table, columns: 1, chartDOMs});

    setRendering(false);
  };

  if (isDataDenied("gps")) {
    return (
      <>
        <Navbar />
        <DataAccessDenied streams={["gps"]} />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <HelpButton page="Baselines" />
      <div className="search-divider"></div>

      <Space>
        <DatePicker
          value={date ? dayjs(date, dateFormat) : null}
          onChange={onDateChange}
        />
        {sport === "football" ? (
          <div>
            <PositionSelect
              value={position}
              handleChange={positionChange}
              options={positions}
              width={150}
              placeholder="Full Team"
              sport={sport}
            />
            <Select
              showSearch
              optionFilterProp="value"
              allowClear
              placeholder="All Groups"
              value={playerGroup}
              style={{ width: 150, marginLeft: "0.5em" }}
              onChange={playerGroupChange}
            >
              {playerGroups.map((player_group, index) => (
                <Option value={player_group.name} key={index}>
                  {player_group.name}
                </Option>
              ))}
            </Select>
          </div>
        ) : (
          <></>
        )}
        <Button type="primary" onClick={get}>
          Search
        </Button>
        <Popconfirm
          placement="bottom"
          title="Are you sure?"
          onConfirm={email}
          okText="Yes"
          cancelText="No"
        >
          <Button>Email</Button>
        </Popconfirm>
        <Button loading={rendering} type="primary" onClick={handleDownloadPDF}>
          Export as PDF
        </Button>
      </Space>
      <div className="data-divider">
        <Divider />
      </div>

      {data.length !== 0 && data.data.length !== 0 ? (
        <>
          <span>Sort By: </span>
          <Select
            defaultValue="last_name"
            value={sortBy}
            style={{ width: 200 }}
            onChange={sortByChange}
            showSearch
            optionFilterProp="value"
          >
            <Option value="last_name">Name</Option>
            <Option value="position">Position</Option>

            {data.metrics.map((metric_obj) => (
              <Option value={metric_obj.key} key={metric_obj.key}>
                {metric_obj.name}
              </Option>
            ))}
          </Select>
        </>
      ) : (
        <></>
      )}

      <div className="w-100 h-75">
        {data.length !== 0 && data.data.length !== 0 ? (
          <p className="teamdata-graph-titles" style={{ marginTop: "1em" }}>
            Player Baselines (%)
          </p>
        ) : (
          <></>
        )}
        {data.length !== 0 && data.data.length !== 0 ? (
          <BaselineChart
            dataIn={data}
            teamColors={teamColors}
            player_data={false}
            dataColorUtil={dataColorUtil}
          ></BaselineChart>
        ) : (
          <Empty description="No data available for your query" />
        )}
      </div>
    </>
  );
};
